import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiService, ApiError } from '../services/api'
import type { AdoptionRate, UserSegmentation, TimeSaved, FeatureGap, ActiveAccountAdoption } from '../types'
import { formatNumber } from '../utils/formatters'
import TabNavigation from './TabNavigation'
import UsageChart from './UsageChart'
import TimeSeriesChart from './TimeSeriesChart'
import UserTypesChart from './UserTypesChart'
import StatusSummary from './StatusSummary'
import { useDateRange } from '../context/DateRangeContext'
import { useNotification } from '../context/NotificationContext'
import { useLanguage } from '../context/LanguageContext'

export default function ManagerDashboard() {
  const [adoptionRate, setAdoptionRate] = useState<AdoptionRate | null>(null)
  const [activeAdoption, setActiveAdoption] = useState<ActiveAccountAdoption | null>(null)
  const [segmentation, setSegmentation] = useState<UserSegmentation | null>(null)
  const [timeSaved, setTimeSaved] = useState<TimeSaved | null>(null)
  const [featureGaps, setFeatureGaps] = useState<FeatureGap[]>([])
  const [allUsers, setAllUsers] = useState<string[]>([])
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [userSearchOpen, setUserSearchOpen] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  // Individual loading states
  const [loadingAdoption, setLoadingAdoption] = useState(true)
  const [loadingActiveAdoption, setLoadingActiveAdoption] = useState(true)
  const [loadingSegmentation, setLoadingSegmentation] = useState(true)
  const [loadingTimeSaved, setLoadingTimeSaved] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(true)

  const { startDate, endDate, comparisonLabel } = useDateRange()
  const { showNotification } = useNotification()
  const { t } = useLanguage()

  // Load each data type independently
  useEffect(() => {
    if (!startDate || !endDate) return

    // Load adoption rate
    setLoadingAdoption(true)
    apiService.getAdoptionRateByRange(startDate, endDate)
      .then(data => setAdoptionRate(data))
      .catch(error => {
        console.error('Error loading adoption rate:', error)
        if (error instanceof ApiError) {
          const [title, ...messageParts] = error.userMessage.split(': ')
          showNotification('error', title, messageParts.join(': '))
        }
      })
      .finally(() => setLoadingAdoption(false))

    // Load active account adoption
    setLoadingActiveAdoption(true)
    apiService.getActiveAccountAdoption(startDate, endDate)
      .then(data => setActiveAdoption(data))
      .catch(error => {
        console.error('Error loading active account adoption:', error)
        if (error instanceof ApiError) {
          const [title, ...messageParts] = error.userMessage.split(': ')
          showNotification('error', title, messageParts.join(': '))
        }
      })
      .finally(() => setLoadingActiveAdoption(false))

    // Load segmentation
    setLoadingSegmentation(true)
    apiService.getUserSegmentationByRange(startDate, endDate)
      .then(data => setSegmentation(data))
      .catch(error => {
        console.error('Error loading segmentation:', error)
        if (error instanceof ApiError) {
          const [title, ...messageParts] = error.userMessage.split(': ')
          showNotification('error', title, messageParts.join(': '))
        }
      })
      .finally(() => setLoadingSegmentation(false))

    // Load time saved
    setLoadingTimeSaved(true)
    apiService.getTimeSavedByRange(startDate, endDate)
      .then(data => setTimeSaved(data))
      .catch(error => {
        console.error('Error loading time saved:', error)
        if (error instanceof ApiError) {
          const [title, ...messageParts] = error.userMessage.split(': ')
          showNotification('error', title, messageParts.join(': '))
        }
      })
      .finally(() => setLoadingTimeSaved(false))

    // Load users
    setLoadingUsers(true)
    apiService.getUserActivityByRange(startDate, endDate, 1000)
      .then(users => {
        const uniqueUsers = Array.from(new Set(users.map((u: any) => u.user_email))).sort()
        setAllUsers(uniqueUsers)
      })
      .catch(error => {
        console.error('Error loading users:', error)
        if (error instanceof ApiError) {
          const [title, ...messageParts] = error.userMessage.split(': ')
          showNotification('error', title, messageParts.join(': '))
        }
      })
      .finally(() => setLoadingUsers(false))
  }, [startDate, endDate])

  const isAnyLoading = loadingAdoption || loadingSegmentation || loadingTimeSaved

  return (
    <>
      <TabNavigation />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="glass-card rounded-2xl p-6 mb-6">
            <h1 className="text-3xl font-bold text-gray-800">{t.dashboard.title}</h1>
            <p className="text-gray-600 mt-1">{t.dashboard.subtitle}</p>
          </div>

          {/* Status Summary - shows skeleton while loading */}
          <StatusSummary
            adoptionRate={adoptionRate}
            segmentation={segmentation}
            timeSaved={timeSaved}
            loading={isAnyLoading}
          />

          {/* Active Account Adoption */}
          <div className="glass-card rounded-2xl p-6 mb-6 fade-in" style={{ animationDelay: '0.05s' }}>
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t.activeAdoption.title}</h2>
            {loadingActiveAdoption || !activeAdoption ? (
              <div className="space-y-3">
                <div className="h-10 w-32 bg-gray-300 rounded animate-pulse" />
                <div className="h-4 w-64 bg-gray-300 rounded animate-pulse" />
              </div>
            ) : (
              <div>
                <div className="text-4xl font-bold text-gray-900">
                  {activeAdoption.adoption_rate}%
                </div>
                <p className="text-gray-600 mt-2">
                  {t.activeAdoption.subtitle
                    .replace('{adopters}', formatNumber(activeAdoption.active_adopters))
                    .replace('{accounts}', formatNumber(activeAdoption.active_accounts))}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {t.activeAdoption.note.replace('{days}', String(activeAdoption.inactive_days))}
                </p>
              </div>
            )}
          </div>

        {/* Time Series Chart - Usage Over Time */}
        <div className="glass-card rounded-2xl p-6 mb-6 fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.dashboard.usageOverTime}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {t.dashboard.usageOverTimeDesc}
              </p>
            </div>

            {/* User Filter Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  // If clicking the X, don't toggle dropdown
                  if ((e.target as HTMLElement).classList.contains('clear-user')) {
                    setSelectedUser('')
                    setUserSearch('')
                  } else {
                    setUserSearchOpen(!userSearchOpen)
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {selectedUser ? selectedUser : t.dashboard.allUsers}
                {selectedUser && (
                  <span
                    className="clear-user ml-1 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    ×
                  </span>
                )}
              </button>

              {userSearchOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
                  <div className="p-3 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder={t.dashboard.searchUser}
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedUser('')
                        setUserSearch('')
                        setUserSearchOpen(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b border-gray-100"
                    >
                      <span className="font-medium text-gray-900">{t.dashboard.allUsers}</span>
                    </button>
                    {allUsers
                      .filter(email => email.toLowerCase().includes(userSearch.toLowerCase()))
                      .map((email) => (
                        <button
                          key={email}
                          onClick={() => {
                            setSelectedUser(email)
                            setUserSearch('')
                            setUserSearchOpen(false)
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            selectedUser === email ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {email}
                        </button>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
          <TimeSeriesChart selectedUser={selectedUser} />
        </div>

        {/* User Types Over Time Chart */}
        <div className="glass-card rounded-2xl p-6 mb-6 fade-in" style={{ animationDelay: '0.15s' }}>
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              {t.userTypesChart.title}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {t.userTypesChart.subtitle}
            </p>
          </div>
          <UserTypesChart />
        </div>

        {/* Usage Breakdown Chart */}
        <div className="glass-card rounded-2xl p-6 fade-in" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t.dashboard.usageByAppAction}
          </h2>
          <UsageChart selectedUser={selectedUser} />
        </div>

      </div>
      </div>
    </>
  )
}
